import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';

const DataTableComponent = ({ 
  records = [], 
  onDelete, 
  loading = false,
  totalRecords = 0,
  currentPage = 1,
  pageSize = 20,
  onPageChange 
}) => {
  // Format currency values
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value);
  };

  // Format date
  const formatDate = (value) => {
    if (!value) return '-';
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-IN');
      }
    } catch (e) {
      // If parsing fails, return original value
    }
    return value;
  };

  // Action buttons template
  const actionBodyTemplate = (rowData) => {
    return (
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-danger p-button-text"
        onClick={() => onDelete(rowData.loanId)}
        tooltip="Delete Record"
        tooltipOptions={{ position: 'top' }}
      />
    );
  };

  // Status template based on total amount
  const statusBodyTemplate = (rowData) => {
    if (!rowData) return <Tag severity="info" value="No Data" />;
    const total = parseFloat(rowData.total) || 0;
    if (total > 100000) {
      return <Tag severity="danger" value="High Risk" />;
    } else if (total > 50000) {
      return <Tag severity="warning" value="Medium Risk" />;
    } else if (total > 0) {
      return <Tag severity="success" value="Low Risk" />;
    }
    return <Tag severity="info" value="No Data" />;
  };

  // Email template with truncation
  const emailBodyTemplate = (rowData) => {
    if (!rowData) return '-';
    const email = rowData.email;
    if (!email) return '-';
    return (
      <span className="email-cell" title={email}>
        {email.length > 30 ? `${email.substring(0, 27)}...` : email}
      </span>
    );
  };

  // Mobile number formatter
  const mobileBodyTemplate = (rowData) => {
    if (!rowData) return '-';
    const mobile = rowData.mobileno;
    if (!mobile) return '-';
    const mobileStr = mobile.toString();
    if (mobileStr.length === 10) {
      return `${mobileStr.slice(0, 5)}-${mobileStr.slice(5)}`;
    } else if (mobileStr.length === 12 && mobileStr.startsWith('91')) {
      return `+91 ${mobileStr.slice(2, 7)}-${mobileStr.slice(7)}`;
    }
    return mobileStr;
  };

  // Create amount template with display name
  const createAmountTemplate = (fieldName) => {
    const AmountTemplate = ({ rowData }) => {
      if (!rowData) return '-';
      const value = rowData[fieldName];
      if (!value && value !== 0) return '-';
      const numericValue = parseFloat(value);
      if (numericValue > 100000) {
        return <span className="high-amount">{formatCurrency(value)}</span>;
      } else if (numericValue > 50000) {
        return <span className="medium-amount">{formatCurrency(value)}</span>;
      }
      return formatCurrency(value);
    };
    AmountTemplate.displayName = `AmountTemplate_${fieldName}`;
    return AmountTemplate;
  };

  // Create amount templates for each field
  const PrincipalAmountTemplate = createAmountTemplate('principal');
  const InterestAmountTemplate = createAmountTemplate('interest_fee');
  const ServiceFeeTemplate = createAmountTemplate('service_fee');
  const ServiceFeeGSTTemplate = createAmountTemplate('service_fee_gst');
  const LatePaymentFeeTemplate = createAmountTemplate('late_payment_fee');
  const LatePaymentGSTTemplate = createAmountTemplate('late_payment_gst');
  const TotalAmountTemplate = createAmountTemplate('total');
  const SanctionAmountTemplate = createAmountTemplate('sanction_amt');

  // Loading skeleton template
  const loadingTemplate = () => {
    return (
      <div className="p-3">
        <Skeleton width="100%" height="2rem" className="mb-2" />
        <Skeleton width="100%" height="2rem" className="mb-2" />
        <Skeleton width="100%" height="2rem" className="mb-2" />
      </div>
    );
  };

  return (
    <div className="card mt-4">
      <style>
        {`
          .high-amount {
            color: #dc3545;
            font-weight: bold;
          }
          .medium-amount {
            color: #fd7e14;
            font-weight: 500;
          }
          .email-cell {
            display: inline-block;
            max-width: 200px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .p-datatable .p-datatable-tbody > tr > td {
            padding: 0.75rem;
          }
          .p-datatable .p-datatable-thead > tr > th {
            background-color: #f8f9fa;
            font-weight: 600;
          }
        `}
      </style>
      
      <DataTable
        value={records || []}
        paginator={true}
        rows={pageSize}
        first={(currentPage - 1) * pageSize}
        totalRecords={totalRecords}
        rowsPerPageOptions={[10, 20, 50, 100]}
        sortMode="multiple"
        removableSort
        resizableColumns
        columnResizeMode="expand"
        showGridlines
        stripedRows
        className="p-datatable-sm"
        emptyMessage="No records found"
        loading={loading}
        onPage={(e) => {
          const newPage = e.page + 1;
          const newRows = e.rows;
          if (onPageChange) {
            onPageChange(newPage, newRows);
          }
        }}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
      >
        <Column
          field="loanId"
          header="Loan ID"
          sortable
          filter
          filterPlaceholder="Search by Loan ID"
          style={{ minWidth: '120px', fontWeight: 'bold' }}
        />
        
        <Column
          field="customerId"
          header="Customer ID"
          sortable
          filter
          filterPlaceholder="Search by Customer ID"
          style={{ minWidth: '120px' }}
        />
        
        <Column
          field="customerName"
          header="Customer Name"
          sortable
          filter
          filterPlaceholder="Search by Name"
          style={{ minWidth: '180px' }}
        />
        
        <Column
          field="dob"
          header="DOB"
          sortable
          body={(rowData) => rowData ? formatDate(rowData.dob) : '-'}
          style={{ minWidth: '110px' }}
        />
        
        <Column
          field="pancard"
          header="PAN Card"
          sortable
          style={{ minWidth: '110px' }}
        />
        
        <Column
          field="principal"
          header="Principal (₹)"
          sortable
         // body={PrincipalAmountTemplate}
          style={{ minWidth: '120px' }}
        />
        
        <Column
          field="interest_fee"
          header="Interest (₹)"
          sortable
        //  body={InterestAmountTemplate}
          style={{ minWidth: '100px' }}
        />
        
        <Column
          field="service_fee"
          header="Service Fee (₹)"
          sortable
        //  body={ServiceFeeTemplate}
          style={{ minWidth: '110px' }}
        />
        
        <Column
          field="service_fee_gst"
          header="Service Fee GST (₹)"
          sortable
        //body={ServiceFeeGSTTemplate}
          style={{ minWidth: '130px' }}
        />
        
        <Column
          field="late_payment_fee"
          header="Late Fee (₹)"
          sortable
          //body={LatePaymentFeeTemplate}
          style={{ minWidth: '110px' }}
        />
        
        <Column
          field="late_payment_gst"
          header="Late Fee GST (₹)"
          sortable
          //body={LatePaymentGSTTemplate}
          style={{ minWidth: '120px' }}
        />
        
        <Column
          field="total"
          header="Total Amount (₹)"
          sortable
         // body={TotalAmountTemplate}
          style={{ minWidth: '130px', fontWeight: 'bold' }}
        />
        
        <Column
          field="mobileno"
          header="Mobile No"
          sortable
          body={mobileBodyTemplate}
          style={{ minWidth: '120px' }}
        />
        
        <Column
          field="email"
          header="Email"
          body={emailBodyTemplate}
          style={{ minWidth: '200px' }}
        />
        
        <Column
          field="address"
          header="Address"
          style={{ minWidth: '250px' }}
          body={(rowData) => rowData ? (rowData.address || '-') : '-'}
        />
        
        <Column
          field="state"
          header="State"
          sortable
          filter
          filterPlaceholder="Search by State"
          style={{ minWidth: '120px' }}
        />
        
        <Column
          field="giveoutdate"
          header="Give Out Date"
          sortable
          body={(rowData) => rowData ? formatDate(rowData.giveoutdate) : '-'}
          style={{ minWidth: '120px' }}
        />
        
        <Column
          field="sanction_amt"
          header="Sanction Amt (₹)"
          sortable
          body={SanctionAmountTemplate}
          style={{ minWidth: '130px' }}
        />
        
        <Column
          header="Risk Level"
          body={statusBodyTemplate}
          style={{ minWidth: '100px' }}
        />
        
        <Column
          header="Actions"
          body={actionBodyTemplate}
          style={{ minWidth: '80px' }}
          exportable={false}
        />
      </DataTable>
    </div>
  );
};

export default DataTableComponent;